import React from "react";
import { useTranslation } from "react-i18next";
import useScrollToSection from "../../hooks/useScrollToSection.js";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import { trackCTA } from "../../lib/analytics";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * The entity layer of /preview/home-v2 — the section the live homepage has no
 * equivalent of.
 *
 * The homepage today establishes a mood and a price and never once names what
 * Agapaé does in the words a person (or a model) would use to look for it. The
 * five product pages carry all of that terminology, and the homepage links to
 * them only from the footer, where the relationship between them is invisible.
 *
 * So this section is the hub: one paragraph per capability, in the same order
 * the wedding itself runs — invitation, replies, tables, planning, the day —
 * each naming its entity plainly in an `<h3>` and handing off to the page that
 * carries the detail. Every blurb is a compression of that page's own audited
 * overview copy, not a new claim; the plan qualifier beneath is the one from
 * `PLAN_FEATURE_MATRIX` (`invitationProduct` is `pro`-only, the gallery is on
 * every tier), because a hub that lists five capabilities without saying which
 * plan holds them mis-sells four of them.
 *
 * Five links, not nine: the guides are reached from the pages they belong to,
 * and a homepage that lists every URL is a directory rather than a hub.
 *
 * `trackCTA` reuses the existing `cta_click` event with a new `location`
 * value — no new analytics vocabulary — and it reports under the preview's own
 * page name (`useAnalyticsContext` maps the route), so nothing here lands in
 * the homepage's numbers.
 */
const ITEMS = [
  { key: "invitation", href: "/digital-wedding-invitation", cta: "digital_wedding_invitation" },
  { key: "rsvp", href: "/wedding-rsvp", cta: "wedding_rsvp" },
  { key: "seating", href: "/wedding-seating", cta: "wedding_seating" },
  { key: "planning", href: "/wedding-planning", cta: "wedding_planning" },
  { key: "photos", href: "/wedding-photo-sharing", cta: "wedding_photo_sharing" },
];

const CapabilitiesSection = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const analyticsContext = useAnalyticsContext();
  // The plans are on this page, so the link scrolls here rather than borrowing
  // `useScrollToHomeSection`, which would navigate to the live homepage.
  const scrollToPlans = useScrollToSection("pricing");

  return (
    <section className="section topless hv2-section">
      <div className="container">
        <div className="hv2-header">
          <h2 className="heading-50">{t("homeV2.capabilities.heading")}</h2>
          <p className="body-20">{t("homeV2.capabilities.intro")}</p>
        </div>

        <ul className="hv2-list">
          {ITEMS.map(({ key, href, cta }) => (
            <li key={key} className="hv2-item">
              <h3 className="heading-38 hv2-item-title">
                {t(`homeV2.capabilities.items.${key}.title`)}
              </h3>
              <p className="body-15 hv2-item-body">
                {t(`homeV2.capabilities.items.${key}.body`)}
              </p>
              <a
                className="hv2-text-link"
                href={localize(href)}
                onClick={() =>
                  trackCTA("home_capabilities", {
                    ...analyticsContext,
                    ctaName: cta,
                  })
                }
              >
                {t(`homeV2.capabilities.items.${key}.link`)}
              </a>
            </li>
          ))}
        </ul>

        <p className="hv2-note">
          {t("homeV2.capabilities.note")}{" "}
          <a className="hv2-text-link" href="#pricing" onClick={scrollToPlans}>
            {t("homeV2.capabilities.noteLink")}
          </a>
        </p>
      </div>
    </section>
  );
};

export default CapabilitiesSection;
